/**
 * Creators' payouts (specification §5.7, §10): each settled campaign's payout to the signed-in
 * wallet, from every deployment, with what claiming it takes.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Address, Hex } from "viem";
import { useConfig } from "wagmi";

import { api } from "../api/client.js";
import { formatAmount } from "../format.js";
import { callEscrow } from "../wallet/escrow.js";

/** A payout as `GET /api/payouts` describes it. */
export interface Payout {
  chainId: number;
  escrow: string;
  campaignId: string;
  title: string;
  token: { address: string; symbol: string | null; decimals: number | null };
  /** The wallet the escrow pays. The payout's leaf names it. */
  wallet: string;
  /** Base units, as a decimal string. */
  amount: string;
  /** The payout's Merkle leaf and its proof, as the escrow's `claim` takes them. */
  leaf: string;
  proof: string[];
  resultHash: string;
  /** The escrow's `Claimed` event, once paid. */
  claimed: { tx: string; at: string } | null;
  /** Morcast's relayer's work on it, if the creator asked. */
  relay: { status: "requested" | "submitted" | "claimed" | "failed"; txHash: string | null } | null;
}

const PAYOUTS_KEY = ["payouts"] as const;

/** The sum of payouts, per token: "12.5 USDC", "3 USDC + 40 MOR", or "Nothing yet". */
export function formatTotal(payouts: Pick<Payout, "amount" | "token">[]): string {
  const totals = new Map<string, { units: bigint; token: Payout["token"] }>();
  for (const payout of payouts) {
    const entry = totals.get(payout.token.address) ?? { units: 0n, token: payout.token };
    entry.units += BigInt(payout.amount);
    totals.set(payout.token.address, entry);
  }
  if (totals.size === 0) {
    return "Nothing yet";
  }
  return [...totals.values()]
    .map((entry) => formatAmount(entry.units.toString(), entry.token))
    .join(" + ");
}

/** How often payouts are read again while Morcast is claiming one of them. */
const RELAY_POLL_MS = 5_000;

/** Whether Morcast's relayer is still claiming a payout: asked, or sent and not yet mined. */
export function isRelaying(payout: Pick<Payout, "relay">): boolean {
  return payout.relay?.status === "requested" || payout.relay?.status === "submitted";
}

/**
 * The signed-in wallet's payouts, and whether Morcast submits claims for creators. While Morcast
 * is claiming one, they are read again every few seconds, so the page shows the claim once made.
 */
export function usePayouts() {
  return useQuery({
    queryKey: PAYOUTS_KEY,
    queryFn: () => api.get<{ payouts: Payout[]; relayClaims: boolean }>("/api/payouts"),
    refetchInterval: (query) =>
      query.state.data?.payouts.some(isRelaying) ? RELAY_POLL_MS : false,
  });
}

/** Claims a payout from the connected wallet, which pays the gas. */
export function useClaim() {
  const config = useConfig();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payout: Payout) =>
      callEscrow(
        config,
        { chainId: payout.chainId, escrow: payout.escrow as Address },
        {
          functionName: "claim",
          args: [
            BigInt(payout.campaignId),
            payout.wallet as Address,
            BigInt(payout.amount),
            payout.proof as Hex[],
          ],
        },
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: PAYOUTS_KEY }),
  });
}

/** Asks Morcast's relayer to submit a payout's claim. */
export function useRelayClaim() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payout: Payout) =>
      api.post(`/api/payouts/${payout.escrow}/${payout.campaignId}/relay`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: PAYOUTS_KEY }),
  });
}
